import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload, CheckCircle, AlertCircle, Database, FileSpreadsheet,
  Loader2, ChevronRight, Check, ArrowRight, Users, Package,
  DollarSign, Receipt, UserCog, Clock, RotateCcw, Download,
  FileText, X, ChevronDown, Sparkles, Zap, Undo2, CalendarClock,
  Play, Pause, Trash2, Wand2, UtensilsCrossed
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const MODULE_ICONS = {
  customers: Users,
  products: Package,
  transactions: DollarSign,
  expenses: Receipt,
  employees: UserCog,
  menu_items: UtensilsCrossed,
};

const MODULE_COLORS = {
  customers: 'from-blue-500 to-indigo-600',
  products: 'from-emerald-500 to-teal-600',
  transactions: 'from-amber-500 to-orange-500',
  expenses: 'from-rose-500 to-red-600',
  employees: 'from-violet-500 to-purple-600',
  menu_items: 'from-orange-500 to-amber-500',
};

export default function DataImportPage() {
  const [step, setStep] = useState('select'); // select | upload | preview | importing | done
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [progress, setProgress] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [undone, setUndone] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [showSchedules, setShowSchedules] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState('daily');
  const [scheduleLabel, setScheduleLabel] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [transforms, setTransforms] = useState({});
  const [availableTransforms, setAvailableTransforms] = useState([]);
  const [showTransforms, setShowTransforms] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/import/modules').then(r => {
      const mods = r.data.modules || [];
      setModules(mods);
      // Honor ?module=<key> deep-link from other pages (e.g. Menu Items'
      // "Import" button lands here pre-scoped to menu_items).
      try {
        const qp = new URLSearchParams(window.location.search);
        const m = qp.get('module');
        if (m && mods.some(x => x.key === m)) {
          setSelectedModule(m);
        }
      } catch { /* ignore */ }
    }).catch(() => {});
    api.get('/import/history').then(r => setHistory(r.data.history || [])).catch(() => {});
    api.get('/import/schedules').then(r => setSchedules(r.data.schedules || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedModule) {
      api.get(`/import/templates?module=${selectedModule}`).then(r => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
      setSelectedTemplate(null);
    }
  }, [selectedModule]);

  const handleFileSelect = useCallback(async (e) => {
    const selected = e.target.files?.[0];
    if (!selected || !selectedModule) return;
    setFile(selected);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', selected);
    formData.append('module', selectedModule);
    if (selectedTemplate) {
      formData.append('template', selectedTemplate);
    }

    try {
      const resp = await api.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (resp.data.error) {
        toast.error(resp.data.error);
        setUploading(false);
        return;
      }
      setPreview(resp.data);
      setMapping(resp.data.mapping || {});
      setTransforms({});
      setStep('preview');
      // Fetch available transforms
      api.get('/import/transforms').then(r => setAvailableTransforms(r.data.transforms || [])).catch(() => {});
    } catch (err) {
      toast.error('Failed to parse file: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
    e.target.value = '';
  }, [selectedModule, selectedTemplate]);

  const handleFieldMap = (sourceCol, targetField) => {
    setMapping(prev => ({ ...prev, [sourceCol]: targetField || null }));
  };

  const toggleTransform = (sourceCol, ruleKey) => {
    setTransforms(prev => {
      const curr = prev[sourceCol] || [];
      const exists = curr.includes(ruleKey);
      return {
        ...prev,
        [sourceCol]: exists ? curr.filter(r => r !== ruleKey) : [...curr, ruleKey],
      };
    });
  };

  const getTransformsForField = (targetField) => {
    if (!targetField) return [];
    return availableTransforms.filter(t => t.applies_to === '*' || t.applies_to.includes(targetField));
  };

  const handleImport = async () => {
    if (!preview || !file) return;
    setImporting(true);
    setStep('importing');
    setProgress(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('module', selectedModule);
      formData.append('mapping_json', JSON.stringify(mapping));
      formData.append('skip_duplicates', 'true');
      formData.append('transforms_json', JSON.stringify(transforms));

      const resp = await api.post('/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (resp.data.error) {
        toast.error(resp.data.error);
        setStep('preview');
        setImporting(false);
        return;
      }

      const jobId = resp.data.job_id;
      setJobId(jobId);
      // Start polling progress
      pollRef.current = setInterval(async () => {
        try {
          const pr = await api.get(`/import/progress/${jobId}`);
          const job = pr.data;
          if (job.error) return;

          setProgress(job);

          if (job.status === 'done') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setResult({
              imported: job.imported,
              skipped: job.skipped,
              errors: job.errors || [],
              total: job.total_rows,
              failed_count: job.failed_count || 0,
            });
            setStep('done');
            setImporting(false);
            toast.success(`Imported ${job.imported} records!`);
            api.get('/import/history').then(r => setHistory(r.data.history || [])).catch(() => {});
          }
        } catch {}
      }, 750);

    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || err.message));
      setStep('preview');
      setImporting(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const reset = () => {
    setStep('select');
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    setSelectedModule(null);
    setSelectedTemplate(null);
    setProgress(null);
    setTransforms({});
    setShowTransforms(false);
    setJobId(null);
    setUndone(false);
    setUndoing(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const ModIcon = selectedModule ? (MODULE_ICONS[selectedModule] || Database) : Database;
  const modColor = selectedModule ? (MODULE_COLORS[selectedModule] || 'from-gray-500 to-gray-600') : 'from-emerald-500 to-teal-600';

  const downloadSample = (templateKey) => {
    const baseUrl = api.defaults.baseURL || '';
    const url = `${baseUrl}/import/templates/${templateKey}/sample`;
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Per-module blank sample (no specific software template) — gives users
  // a ready-to-fill CSV with all expected columns the moment they pick a
  // module. Especially useful for menu_items where there's no third-party
  // export to download.
  const downloadModuleSample = (moduleKey) => {
    const baseUrl = api.defaults.baseURL || '';
    const url = `${baseUrl}/import/modules/${moduleKey}/sample`;
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadFailedRows = async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`/import/jobs/${jobId}/failed-rows`, {
        responseType: 'blob'
      });
      
      // Check if response is an error (JSON)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const json = JSON.parse(text);
        if (json.error) {
          toast.error(json.error);
          return;
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${preview?.filename?.replace(/\.[^/.]+$/, '') || 'import'}_failed_rows.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUndo = async () => {
    if (!jobId || undone || undoing) return;
    setUndoing(true);
    try {
      const resp = await api.post(`/import/jobs/${jobId}/undo`);
      if (resp.data.error) {
        toast.error(resp.data.error);
      } else {
        setUndone(true);
        toast.success(`Rolled back ${resp.data.deleted_count} records`);
        api.get('/import/history').then(r => setHistory(r.data.history || [])).catch(() => {});
      }
    } catch (err) {
      toast.error('Undo failed: ' + (err.response?.data?.detail || err.message));
    }
    setUndoing(false);
  };

  const handleCreateSchedule = async () => {
    if (!file || !selectedModule || scheduling) return;
    setScheduling(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('module', selectedModule);
      formData.append('mapping_json', JSON.stringify(mapping));
      formData.append('interval', scheduleInterval);
      formData.append('skip_duplicates', 'true');
      formData.append('label', scheduleLabel);
      if (selectedTemplate) formData.append('template', selectedTemplate);

      const resp = await api.post('/import/schedules', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (resp.data.error) {
        toast.error(resp.data.error);
      } else {
        toast.success(`Schedule created! Runs ${scheduleInterval}`);
        setScheduleForm(false);
        api.get('/import/schedules').then(r => setSchedules(r.data.schedules || [])).catch(() => {});
      }
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
    setScheduling(false);
  };

  const toggleSchedule = async (id) => {
    try {
      const resp = await api.patch(`/import/schedules/${id}/toggle`);
      if (resp.data.error) { toast.error(resp.data.error); return; }
      toast.success(resp.data.active ? 'Schedule resumed' : 'Schedule paused');
      api.get('/import/schedules').then(r => setSchedules(r.data.schedules || [])).catch(() => {});
    } catch (err) { toast.error('Failed'); }
  };

  const deleteSchedule = async (id) => {
    try {
      const resp = await api.delete(`/import/schedules/${id}`);
      if (resp.data.error) { toast.error(resp.data.error); return; }
      toast.success('Schedule deleted');
      setSchedules(prev => prev.filter(s => s.schedule_id !== id));
    } catch (err) { toast.error('Failed'); }
  };

  const runScheduleNow = async (id) => {
    try {
      const resp = await api.post(`/import/schedules/${id}/run-now`);
      if (resp.data.error) { toast.error(resp.data.error); return; }
      toast.success(`Imported ${resp.data.imported} records`);
      api.get('/import/schedules').then(r => setSchedules(r.data.schedules || [])).catch(() => {});
      api.get('/import/history').then(r => setHistory(r.data.history || [])).catch(() => {});
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 p-6 md:p-8" data-testid="data-import-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${modColor} flex items-center justify-center shadow-lg`}>
              <ModIcon size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#1D1D1F]" style={{ fontFamily: "'Outfit', sans-serif" }}>Easy Data Import</h1>
              <p className="text-sm text-[#86868B] mt-0.5">Import from POS, Accounting, or any spreadsheet</p>
            </div>
          </div>
          {(history.length > 0 || schedules.length > 0) && (
            <div className="flex items-center gap-2">
              {schedules.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => { setShowSchedules(!showSchedules); setShowHistory(false); }} data-testid="toggle-schedules-btn">
                  <CalendarClock size={14} className="mr-1.5" />
                  Schedules ({schedules.filter(s => s.active).length})
                  <ChevronDown size={14} className={`ml-1 transition-transform ${showSchedules ? 'rotate-180' : ''}`} />
                </Button>
              )}
              {history.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => { setShowHistory(!showHistory); setShowSchedules(false); }} data-testid="toggle-history-btn">
                  <Clock size={14} className="mr-1.5" />
                  History ({history.length})
                  <ChevronDown size={14} className={`ml-1 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Import History */}
        {showHistory && history.length > 0 && (
          <Card data-testid="import-history">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock size={16} /> Import History</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 text-sm">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={14} className="text-success" />
                      <span className="font-medium text-[#1D1D1F]">{h.module_label}</span>
                      <span className="text-[#86868B]">{h.filename}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#86868B]">
                      <span className="text-success font-medium">+{h.imported}</span>
                      {h.skipped > 0 && <span>{h.skipped} skipped</span>}
                      <span>{new Date(h.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedules Panel */}
        {showSchedules && schedules.length > 0 && (
          <Card data-testid="schedules-panel">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><CalendarClock size={16} /> Scheduled Imports</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {schedules.map(s => (
                  <div key={s.schedule_id} className={`p-3 rounded-lg border text-sm ${s.active ? 'border-gray-100 hover:bg-gray-50' : 'border-gray-100 bg-gray-50 opacity-60'}`} data-testid={`schedule-${s.schedule_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CalendarClock size={14} className={s.active ? 'text-info' : 'text-gray-400'} />
                        <div>
                          <p className="font-medium text-[#1D1D1F]">{s.label}</p>
                          <p className="text-[10px] text-[#86868B]">
                            {s.filename} &middot; {s.row_count} rows &middot; {s.interval}
                            {s.last_run_at ? ` \u00b7 Last: ${new Date(s.last_run_at).toLocaleDateString()}` : ''}
                            {s.run_count > 0 ? ` \u00b7 ${s.run_count} runs` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {s.last_result && (
                          <span className="text-[10px] text-success font-medium mr-2">+{s.last_result.imported}</span>
                        )}
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => runScheduleNow(s.schedule_id)}
                          title="Run now"
                          data-testid={`run-now-${s.schedule_id}`}
                        >
                          <Play size={12} className="text-success" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => toggleSchedule(s.schedule_id)}
                          title={s.active ? 'Pause' : 'Resume'}
                          data-testid={`toggle-${s.schedule_id}`}
                        >
                          {s.active ? <Pause size={12} className="text-warning" /> : <Play size={12} className="text-info" />}
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => deleteSchedule(s.schedule_id)}
                          title="Delete"
                          data-testid={`delete-${s.schedule_id}`}
                        >
                          <Trash2 size={12} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Steps indicator */}
        <div className="flex items-center gap-2 text-sm" data-testid="import-steps">
          <StepBadge num={1} label="Select Module" active={step === 'select'} done={step !== 'select'} />
          <ChevronRight size={14} className="text-gray-300" />
          <StepBadge num={2} label="Upload File" active={step === 'upload'} done={['preview', 'importing', 'done'].includes(step)} />
          <ChevronRight size={14} className="text-gray-300" />
          <StepBadge num={3} label="Map & Preview" active={step === 'preview'} done={['importing', 'done'].includes(step)} />
          <ChevronRight size={14} className="text-gray-300" />
          <StepBadge num={4} label="Import" active={step === 'importing'} done={step === 'done'} />
        </div>

        {/* Step 1: Select Module */}
        {step === 'select' && (
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">What data are you importing?</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {modules.map(mod => {
                    const Icon = MODULE_ICONS[mod.key] || Database;
                    const color = MODULE_COLORS[mod.key] || 'from-gray-500 to-gray-600';
                    const isSelected = selectedModule === mod.key;
                    return (
                      <button
                        key={mod.key}
                        onClick={() => setSelectedModule(mod.key)}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-success bg-success/10 shadow-sm'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`module-${mod.key}`}
                      >
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#1D1D1F]">{mod.label}</p>
                          <p className="text-[11px] text-[#86868B]">{mod.fields.length} fields</p>
                        </div>
                        {isSelected && <Check size={18} className="text-success ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="bg-info/10 border border-info/30 rounded-lg p-4 text-info text-sm space-y-2">
                  <p className="font-semibold">Supported Formats</p>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><FileSpreadsheet size={14} /> CSV (.csv)</span>
                    <span className="flex items-center gap-1"><FileSpreadsheet size={14} /> Excel (.xlsx)</span>
                    <span className="flex items-center gap-1"><FileText size={14} /> JSON (.json)</span>
                  </div>
                  <p className="text-xs text-info">Export from any POS (Square, Clover, Toast), accounting software (QuickBooks, Xero, Zoho), or plain spreadsheet.</p>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={() => setStep('upload')}
              disabled={!selectedModule}
              className="w-full bg-success hover:bg-emerald-700"
              data-testid="next-to-upload-btn"
            >
              Continue <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
        )}

        {/* Step 2: Upload File */}
        {step === 'upload' && (
          <div className="space-y-4">
            {/* Blank module template — works for any module, especially
                useful for menu_items where there's no third-party export
                to download. Gives users a ready-to-fill CSV with the
                exact columns the importer expects. */}
            {selectedModule && (
              <div className="flex items-center justify-between p-3 rounded-xl border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10 dark:border-emerald-800" data-testid="blank-template-strip">
                <div className="flex items-center gap-2.5">
                  <Download size={15} className="text-emerald-600" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Don't have a file yet?</p>
                    <p className="text-[11px] text-emerald-700/70 dark:text-emerald-400/70">Download the blank sample CSV with all expected columns.</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadModuleSample(selectedModule)}
                  className="rounded-lg bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="download-blank-template"
                >
                  <Download size={13} className="mr-1.5" /> Download template
                </Button>
              </div>
            )}
            {/* Template Picker */}
            {templates.length > 0 && (
              <Card data-testid="template-picker">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles size={16} className="text-warning" />
                    Smart Templates
                    <Badge variant="secondary" className="text-[10px] ml-1">One-Click Mapping</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-[#86868B] mb-3">Select your software for instant column mapping, or skip for auto-detect.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {templates.map(tpl => {
                      const isSelected = selectedTemplate === tpl.key;
                      return (
                        <button
                          key={tpl.key}
                          onClick={() => setSelectedTemplate(isSelected ? null : tpl.key)}
                          className={`flex items-center gap-2.5 p-3 rounded-lg border-2 transition-all text-left ${
                            isSelected
                              ? 'border-amber-400 bg-warning/10 shadow-sm'
                              : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                          }`}
                          data-testid={`template-${tpl.key}`}
                        >
                          <Zap size={16} className={isSelected ? 'text-warning' : 'text-gray-400'} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#1D1D1F] truncate">{tpl.software}</p>
                            <p className="text-[10px] text-[#86868B] truncate">{tpl.description}</p>
                          </div>
                          {isSelected && <Check size={14} className="text-warning ml-auto shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTemplate && (() => {
                    const tpl = templates.find(t => t.key === selectedTemplate);
                    return tpl ? (
                      <div className="mt-3 space-y-2">
                        {tpl.file_hint && (
                          <div className="p-2.5 rounded-lg bg-warning/10 border border-warning/30 text-xs text-warning" data-testid="template-hint">
                            <span className="font-semibold">How to export:</span> {tpl.file_hint}
                          </div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadSample(tpl.key); }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-success/30 bg-success/10 hover:bg-success/20 transition-colors text-xs font-medium text-success w-full justify-center"
                          data-testid="download-sample-btn"
                        >
                          <Download size={14} />
                          Download Sample CSV — see the expected format
                        </button>
                      </div>
                    ) : null;
                  })()}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="pt-6">
                <label className="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-success/10 transition-all" data-testid="upload-area">
                  <div className="flex flex-col items-center">
                    {uploading ? (
                      <>
                        <Loader2 size={36} className="text-success animate-spin mb-3" />
                        <span className="text-sm font-medium text-[#86868B]">Analyzing your file...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={36} className="text-gray-300 mb-3" />
                        <span className="text-sm font-medium text-[#1D1D1F]">Click to upload your file</span>
                        <span className="text-xs text-[#86868B] mt-1">CSV, Excel (.xlsx), or JSON</span>
                        {selectedTemplate ? (
                          <span className="text-xs text-warning mt-2 font-medium flex items-center gap-1">
                            <Zap size={12} /> {templates.find(t => t.key === selectedTemplate)?.software} template active
                          </span>
                        ) : (
                          <span className="text-xs text-success mt-2 font-medium">
                            Auto-detect mapping for {modules.find(m => m.key === selectedModule)?.label || selectedModule}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv,.xlsx,.xls,.json"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    data-testid="file-input"
                  />
                </label>
              </CardContent>
            </Card>
            <Button variant="outline" onClick={() => { setStep('select'); setFile(null); setSelectedTemplate(null); }} data-testid="back-to-select-btn">
              <RotateCcw size={14} className="mr-1.5" /> Back
            </Button>
          </div>
        )}

        {/* Step 3: Preview & Map */}
        {step === 'preview' && preview && (
          <div className="space-y-4">
            {/* File Info */}
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet size={20} className="text-success" />
                    <div>
                      <p className="text-sm font-semibold text-[#1D1D1F]">{preview.filename}</p>
                      <p className="text-xs text-[#86868B]">{preview.total_rows} rows &middot; {preview.format.toUpperCase()} &middot; {preview.module_label}</p>
                    </div>
                  </div>
                  {preview.missing_required?.length > 0 && (
                    <Badge variant="destructive" className="text-[10px]" data-testid="missing-fields-badge">
                      Missing: {preview.missing_required.join(', ')}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Column Mapping */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowRight size={16} className="text-success" />
                  Column Mapping
                  {selectedTemplate ? (
                    <Badge className="text-[10px] bg-warning/20 text-warning border border-warning/30 ml-2" data-testid="template-active-badge">
                      <Zap size={10} className="mr-1" />
                      {templates.find(t => t.key === selectedTemplate)?.software} template
                    </Badge>
                  ) : (
                    <span className="text-xs text-[#86868B] font-normal ml-2">Auto-detected. Adjust if needed.</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {preview.headers.map(header => (
                    <div key={header} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50" data-testid={`mapping-${header}`}>
                      <span className="text-sm font-medium text-[#1D1D1F] w-40 truncate" title={header}>{header}</span>
                      <ArrowRight size={14} className="text-gray-400 shrink-0" />
                      <select
                        value={mapping[header] || ''}
                        onChange={e => handleFieldMap(header, e.target.value)}
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none"
                        data-testid={`mapping-select-${header}`}
                      >
                        <option value="">-- Skip this column --</option>
                        {preview.available_fields.map(f => (
                          <option key={f} value={f}>{f}{preview.required_fields?.includes(f) ? ' *' : ''}</option>
                        ))}
                      </select>
                      {mapping[header] ? (
                        <Check size={16} className="text-success shrink-0" />
                      ) : (
                        <X size={16} className="text-gray-300 shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Field Transformation Rules */}
            {availableTransforms.length > 0 && (
              <Card data-testid="transforms-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wand2 size={16} className="text-violet-500" />
                      Transform Rules
                      <Badge variant="secondary" className="text-[10px] ml-1">Optional</Badge>
                    </CardTitle>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setShowTransforms(!showTransforms)}
                      className="text-xs"
                      data-testid="toggle-transforms-btn"
                    >
                      {showTransforms ? 'Hide' : 'Configure'}
                      <ChevronDown size={12} className={`ml-1 transition-transform ${showTransforms ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                {showTransforms && (
                  <CardContent>
                    <p className="text-xs text-[#86868B] mb-3">Auto-transform data during import: format phones, convert currencies, fix dates, and more.</p>
                    <div className="space-y-3">
                      {preview.headers.filter(h => mapping[h]).map(header => {
                        const target = mapping[header];
                        const rules = getTransformsForField(target);
                        if (rules.length === 0) return null;
                        const active = transforms[header] || [];
                        return (
                          <div key={header} className="p-3 rounded-lg border border-gray-100" data-testid={`transform-${header}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-[#1D1D1F]">{header}</span>
                              <ArrowRight size={10} className="text-gray-400" />
                              <span className="text-xs text-violet-600 font-medium">{target}</span>
                              {active.length > 0 && (
                                <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200">{active.length} active</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {rules.map(rule => {
                                const isActive = active.includes(rule.key);
                                return (
                                  <button
                                    key={rule.key}
                                    onClick={() => toggleTransform(header, rule.key)}
                                    className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all border ${
                                      isActive
                                        ? 'bg-violet-100 border-violet-300 text-violet-700'
                                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                    }`}
                                    title={rule.desc}
                                    data-testid={`transform-rule-${header}-${rule.key}`}
                                  >
                                    {isActive && <Check size={10} className="inline mr-1" />}
                                    {rule.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {Object.values(transforms).some(arr => arr.length > 0) && (
                      <div className="mt-3 p-2.5 rounded-lg bg-violet-50 border border-violet-200 text-xs text-violet-700" data-testid="transforms-summary">
                        <Wand2 size={12} className="inline mr-1" />
                        {Object.entries(transforms).filter(([, v]) => v.length > 0).map(([col, rules]) => (
                          <span key={col} className="mr-2">{col}: {rules.length} rule{rules.length > 1 ? 's' : ''}</span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            )}

            {/* Data Preview */}
            <Card>
              <CardHeader><CardTitle className="text-base">Data Preview (first 5 rows)</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {preview.headers.map(h => (
                          <th key={h} className="text-left p-2 font-semibold text-[#86868B] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_rows.map((row, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          {preview.headers.map(h => (
                            <td key={h} className="p-2 text-[#1D1D1F] whitespace-nowrap max-w-[200px] truncate">{String(row[h] ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => { setStep('upload'); setPreview(null); setFile(null); }} data-testid="back-to-upload-btn">
                <RotateCcw size={14} className="mr-1.5" /> Re-upload
              </Button>
              <Button
                onClick={handleImport}
                disabled={preview.missing_required?.length > 0}
                className="flex-1 bg-success hover:bg-emerald-700"
                data-testid="confirm-import-btn"
              >
                <Download size={14} className="mr-1.5" />
                Import {preview.total_rows.toLocaleString()} Records
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Importing with Progress */}
        {step === 'importing' && (
          <Card data-testid="import-progress-card">
            <CardContent className="pt-8 pb-8">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${modColor} flex items-center justify-center`}>
                      <ModIcon size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-[#1D1D1F]">
                        Importing into {modules.find(m => m.key === selectedModule)?.label || selectedModule}
                      </p>
                      <p className="text-xs text-[#86868B]">{preview?.filename}</p>
                    </div>
                  </div>
                  <Loader2 size={20} className="text-success animate-spin" />
                </div>

                {/* Progress Bar */}
                {(() => {
                  const total = progress?.total_rows || preview?.total_rows || 1;
                  const processed = progress?.processed || 0;
                  const pct = Math.min(100, Math.round((processed / total) * 100));
                  const eta = progress?.eta_seconds;
                  const rate = progress?.rows_per_sec;

                  return (
                    <div className="space-y-3">
                      {/* Bar */}
                      <div className="relative w-full h-4 bg-gray-100 rounded-full overflow-hidden" data-testid="progress-bar">
                        <div
                          className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${pct}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white mix-blend-difference" data-testid="progress-pct">{pct}%</span>
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="flex items-center justify-between text-xs text-[#86868B]">
                        <span data-testid="progress-rows">{processed.toLocaleString()} / {total.toLocaleString()} rows</span>
                        <span data-testid="progress-rate">{rate ? `${rate} rows/sec` : 'Starting...'}</span>
                      </div>

                      {/* Live Counters */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg bg-success/10 border border-success/30 text-center">
                          <p className="text-lg font-bold text-success" data-testid="progress-imported">{(progress?.imported || 0).toLocaleString()}</p>
                          <p className="text-[10px] text-success font-medium">Imported</p>
                        </div>
                        <div className="p-3 rounded-lg bg-warning/10 border border-warning/30 text-center">
                          <p className="text-lg font-bold text-warning" data-testid="progress-skipped">{(progress?.skipped || 0).toLocaleString()}</p>
                          <p className="text-[10px] text-warning font-medium">Skipped</p>
                        </div>
                        <div className="p-3 rounded-lg bg-error/10 border border-error/30 text-center">
                          <p className="text-lg font-bold text-error" data-testid="progress-errors">{(progress?.error_count || 0).toLocaleString()}</p>
                          <p className="text-[10px] text-error font-medium">Errors</p>
                        </div>
                      </div>

                      {/* ETA */}
                      {eta != null && eta > 0 && (
                        <div className="flex items-center justify-center gap-1.5 text-xs text-[#86868B]" data-testid="progress-eta">
                          <Clock size={12} />
                          <span>
                            {eta >= 60
                              ? `~${Math.ceil(eta / 60)} min remaining`
                              : `~${Math.ceil(eta)} sec remaining`
                            }
                          </span>
                        </div>
                      )}

                      <p className="text-center text-[10px] text-[#86868B]">Please don't close this page</p>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Done */}
        {step === 'done' && result && (
          <div className="space-y-4">
            {/* Success / Undone Banner */}
            {undone ? (
              <Card className="border-gray-300 bg-gray-50" data-testid="undo-done-card">
                <CardContent className="pt-6 pb-4">
                  <div className="flex items-center gap-3">
                    <Undo2 size={32} className="text-gray-500" />
                    <div>
                      <p className="text-xl font-bold text-gray-700">Import Rolled Back</p>
                      <p className="text-sm text-[#86868B] mt-0.5">
                        All {result.imported} imported records have been removed
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-success/30 bg-success/10">
                <CardContent className="pt-6 pb-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={32} className="text-success" />
                    <div>
                      <p className="text-xl font-bold text-success">Import Complete!</p>
                      <p className="text-sm text-[#86868B] mt-0.5">
                        {result.imported} imported, {result.skipped} skipped
                        {result.errors?.length > 0 ? `, ${result.errors.length} errors` : ''}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Undo Import */}
            {!undone && result.imported > 0 && (
              <Card className="border-error/30" data-testid="undo-import-card">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Undo2 size={16} className="text-error" />
                      <div>
                        <p className="text-sm font-medium text-[#1D1D1F]">Made a mistake?</p>
                        <p className="text-[11px] text-[#86868B]">Remove all {result.imported} records imported in this batch</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleUndo}
                      disabled={undoing}
                      variant="outline"
                      size="sm"
                      className="border-error/30 text-error hover:bg-error/10 hover:text-error"
                      data-testid="undo-import-btn"
                    >
                      {undoing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Undo2 size={14} className="mr-1.5" />}
                      {undoing ? 'Undoing...' : 'Undo Import'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Schedule This Import */}
            {!undone && result.imported > 0 && file && (
              <Card className="border-info/30" data-testid="schedule-import-card">
                <CardContent className="pt-4 pb-4">
                  {!scheduleForm ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CalendarClock size={16} className="text-info" />
                        <div>
                          <p className="text-sm font-medium text-[#1D1D1F]">Repeat this import?</p>
                          <p className="text-[11px] text-[#86868B]">Schedule recurring imports with the same file and mapping</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setScheduleForm(true)}
                        variant="outline"
                        size="sm"
                        className="border-info/30 text-info hover:bg-info/10"
                        data-testid="open-schedule-form-btn"
                      >
                        <CalendarClock size={14} className="mr-1.5" /> Schedule
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <CalendarClock size={16} className="text-info" />
                        <p className="text-sm font-semibold text-[#1D1D1F]">Schedule Recurring Import</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[11px] text-[#86868B] mb-1 block">Frequency</label>
                          <select
                            value={scheduleInterval}
                            onChange={e => setScheduleInterval(e.target.value)}
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-200 outline-none"
                            data-testid="schedule-interval-select"
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Every 2 Weeks</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[11px] text-[#86868B] mb-1 block">Label (optional)</label>
                          <input
                            type="text"
                            value={scheduleLabel}
                            onChange={e => setScheduleLabel(e.target.value)}
                            placeholder="e.g. Weekly POS sync"
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-200 outline-none"
                            data-testid="schedule-label-input"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={handleCreateSchedule}
                          disabled={scheduling}
                          size="sm"
                          className="bg-info hover:bg-blue-700 flex-1"
                          data-testid="confirm-schedule-btn"
                        >
                          {scheduling ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CalendarClock size={14} className="mr-1.5" />}
                          {scheduling ? 'Creating...' : 'Create Schedule'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setScheduleForm(false)} data-testid="cancel-schedule-btn">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Error Recovery */}
            {!undone && (result.failed_count > 0 || result.skipped > 0 || result.errors?.length > 0) && (
              <Card className="border-warning/30 bg-warning/10" data-testid="error-recovery-card">
                <CardContent className="pt-5 pb-5">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={18} className="text-warning" />
                      <p className="text-sm font-semibold text-warning">
                        {result.failed_count || (result.skipped + (result.errors?.length || 0))} rows need attention
                      </p>
                    </div>

                    {result.errors?.length > 0 && (
                      <div className="space-y-1 text-xs text-error max-h-28 overflow-y-auto p-2 rounded bg-error/10 border border-error/30">
                        {result.errors.map((e, i) => (
                          <div key={i}>Row {e.row}: {e.error}</div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-[#86868B]">
                      Download the failed rows as CSV, fix the issues, and re-import just those records.
                    </p>

                    <Button
                      onClick={downloadFailedRows}
                      variant="outline"
                      className="w-full border-warning/30 text-warning hover:bg-warning/20"
                      data-testid="download-failed-rows-btn"
                    >
                      <Download size={14} className="mr-1.5" />
                      Download Failed Rows CSV ({result.failed_count || (result.skipped + (result.errors?.length || 0))} rows)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button onClick={reset} className="w-full bg-success hover:bg-emerald-700" data-testid="import-again-btn">
              <RotateCcw size={14} className="mr-1.5" /> Import More Data
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StepBadge({ num, label, active, done }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      active ? 'bg-success/20 text-success ring-1 ring-emerald-200' :
      done ? 'bg-success/10 text-success' :
      'bg-gray-50 text-gray-400'
    }`}>
      {done ? <Check size={12} /> : <span className="w-4 h-4 rounded-full bg-current/10 flex items-center justify-center text-[10px]">{num}</span>}
      {label}
    </div>
  );
}
